import { AlCardstackItem, AlSession } from '@al/core';
import { AlDeploymentsClient, Deployment } from '@al/deployments';
import {
    AlExposureNotificationsClient,
    AlNotificationPolicy,
    AssetFilter,
    Subscription
} from '@al/exposure-notifications';
import { AlGenericAlertDefinition } from '@al/gestalt';
import { AlNavigationService } from '@al/ng-navigation-components';
import { SQXSearchQuery } from '@al/search';
import {
    Component,
    Input,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { AssetFilterOption, AssetFilterUnpacker } from '../services/al-health-notification-utility';
import { AlNotificationDictionariesUtility } from '../services/al-notification-dictionaries-utility';

@Component({
    selector: 'al-notification-content',
    templateUrl: './al-notification-content.component.html',
    styleUrls: ['./al-notification-content.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class AlNotificationContentComponent implements OnInit {

    @Input() item!: AlCardstackItem<AlGenericAlertDefinition, any>;
    @Input() isSko2021Beta: boolean = false;
    @Input() accountId:string = "";
    @Input() isFim:boolean = false;

    public alertTypes:Array<string> = [];
    public deploymentsSelected:Array<string> = [];
    public deploymentDictionary:{[i:string]:string} = {};
    public conditions:SQXSearchQuery = new SQXSearchQuery();
    public subscriptionsDictionary: {[subscriptionId: string]: Subscription} = {};
    public healthPolicy: AlNotificationPolicy = {} as AlNotificationPolicy;
    public allAvailableAssets: AssetFilter[] = [];
    public renameConnectors: boolean = false; // TODO: remove this after the connector renaming goes into production
    private policies: AlNotificationPolicy[] = [];
    private dictionaryUtility: AlNotificationDictionariesUtility = new AlNotificationDictionariesUtility();

    constructor(private alNavigation: AlNavigationService){ }

    closeError(item: AlCardstackItem<AlGenericAlertDefinition, any>) {
        item.properties.error = null;
    }

    async ngOnInit(): Promise<void> {
        try {
            await Promise.all([AlSession.resolved(), this.alNavigation.ready()]);
            this.renameConnectors = this.alNavigation.isExperienceAvailableSync("automated-response#rename-connectors"); // TODO: remove this after the connector renaming goes into production
        } catch (err) {
            console.warn(`navigation can't load correctly`);
        }
        if( this.accountId !== "" ) {
            await AlDeploymentsClient.listDeployments(this.accountId).then((response: Deployment[]) => {
                response.forEach( (deployment:Deployment) => {
                    if( deployment.id && deployment.name ) {
                        this.deploymentDictionary[deployment.id] = deployment.name;
                    }
                });
            }, error => {
                console.error(error);
            });
            if (this.item && this.item.entity.type.notificationType === 'health/alerts') {
                await this.getHealthNotificationsDetails();
            }
        }
        if(this.item.properties.filters) {
            this.conditions = SQXSearchQuery.fromJson(this.item.properties.filters);
        }
        this.policies = await AlExposureNotificationsClient.getNotificationPolicies(this.accountId);
    }

    /**
     * getGovernanceText
     * @param value represents value in minutes for suppression interval
     * @returns human redable text
     */
    getGovernanceText(value: number): string {
        let textToShow = "None";
        if (this.accountId) {
            textToShow = this.policies.filter(policy => (policy.schedule.delay/60000) === value).pop()?.name ?? "None";
        }
        return textToShow;
    }

    /**
     * getHealthFilters
     * @param type String: it could be 'notification.agent_status' 'notification.appliance_status' 'notification.appliance_status'.
     * @returns the value label.
     */
    getHealthFilters(type:string) {
        if( this.conditions && type !== "assets" && this.item.properties.filters && this.item.properties.filters !== {} ) {
            try {
                const opFilter = this.conditions.getPropertyCondition(type);
                if(opFilter) {
                    return opFilter.getValues() as string[];
                } else {
                    return [];
                }
            } catch (error) {
                console.warn('getPropertyCondition cannot be used on a query with more than one condition for the target property');
                return [];
            }
        } else if (type === "assets" && this.item.properties.filters && this.item.properties.filters.hasOwnProperty('and')) {
            const unpacker = new AssetFilterUnpacker(this.item.properties.filters['and']);
            let assets: AssetFilterOption[] = unpacker.extractAssetsFromOr();
            const uniqueAssets:AssetFilterOption[] = [];
            const conditions: SQXSearchQuery = SQXSearchQuery.fromJson(this.item.properties.filters);
            if (assets.length === 0) {
                const assetTypeOptions = ["asset.key","asset.deployment_id","asset.deployment","asset.region","asset.vpc","asset.subnet"];
                assetTypeOptions.forEach(type => {
                    const filterOptions = this.dictionaryUtility.getValuesFromConditions(conditions, type).map(key=>{
                        let asset:any = {};
                        asset[type] = key;
                        return asset as AssetFilterOption;
                    });
                    if (filterOptions.length === 1) {
                        uniqueAssets.push(filterOptions[0]);
                    }
                });
            }
            if (uniqueAssets.length>0) {
                assets = assets.concat(uniqueAssets);
            }
            const assetKeys: string[] = assets.map(asset=>Object.values(asset).length>0?Object.values(asset)[0]:"");
            const assetsOptions: string[] = assetKeys.map(
                assetKey => {
                    const someAsset = this.allAvailableAssets.filter(
                        availableAsset => availableAsset.key === assetKey
                    );
                    if (someAsset.length > 0) {
                        if(someAsset[0].type === "deployment") {
                            return someAsset[0].type+" : "+this.deploymentDictionary[someAsset[0]?.deployment_id+""];
                        } else {
                            return someAsset[0].type+" : "+someAsset[0].key;
                        }
                    } else {
                        return "";
                    }
                }
            );
            return assetsOptions.filter( asset => asset!=="" );
        } else {
            return [];
        }
    }

    private async getHealthNotificationsDetails() {
        this.subscriptionsDictionary = {};
        const subscriptions: Subscription[] = await AlExposureNotificationsClient.getSubscriptions(this.accountId);
        subscriptions.forEach(subscription => {
            if (subscription.subscription.hasOwnProperty('id') && subscription.subscription.id) {
                this.subscriptionsDictionary[subscription.subscription.id] = subscription;
            }
        });
        const subscription: Subscription = this.subscriptionsDictionary[this.item.id];
        if (subscription && subscription.notification_policy !== null) {
            const policyId: string = subscription.notification_policy.id;
            this.healthPolicy = await AlExposureNotificationsClient.getNotificationPolicy(this.accountId, policyId);
        }
        // Getting all available assets, we need this to descart assets wich not exist anymore
        const notificationFilters: { [filterName: string]: string[] | AssetFilter[]; }[] = await AlExposureNotificationsClient.getNotificationRules(this.accountId);
        if (notificationFilters && Array.isArray(notificationFilters)) {
            notificationFilters.forEach(notiFilter => {
                if (notiFilter.hasOwnProperty('assets') && Array.isArray(notiFilter['assets'])) {
                    notiFilter['assets'].forEach((filter: string | AssetFilter) => {
                        if (typeof filter !== "string") {
                            this.allAvailableAssets.push(filter);
                        }
                    });
                }
            });
        }
    }

}
